import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { API_ENDPOINTS } from "../../config/api";

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  // Layout
  page: {
    minHeight: "100vh",
    background: "#F4F5F7",
    padding: "2rem 1.75rem",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    color: "#1a1d23",
  },
  // Typography
  pageTitle: { fontSize: 20, fontWeight: 600, color: "#1a1d23", marginBottom: 3 },
  pageSub: { fontSize: 13, color: "#6b7280" },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: "#6b7280",
  },
  // Cards
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    overflow: "hidden",
  },
  cardHead: {
    padding: "0.75rem 1.125rem",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    flexWrap: "wrap",
    background: "#fafafa",
  },
  cardHeadTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "#1a1d23",
    display: "flex",
    alignItems: "center",
    gap: 7,
  },
  cardBody: { padding: "1rem 1.125rem" },
  // KPI
  kpiCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "1rem 1.125rem",
  },
  kpiLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: "#6b7280",
    marginBottom: 6,
  },
  kpiValue: { fontSize: 28, fontWeight: 700, lineHeight: 1, color: "#1a1d23" },
  kpiSub: { fontSize: 12, color: "#9ca3af", marginTop: 5 },
  // Table
  th: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#6b7280",
    padding: "8px 12px",
    textAlign: "left",
    borderBottom: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
    background: "#fafafa",
  },
  td: {
    padding: "9px 12px",
    borderBottom: "1px solid #f3f4f6",
    color: "#1a1d23",
    fontSize: 13,
    verticalAlign: "middle",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  // Segmented control
  seg: {
    display: "flex",
    background: "#f3f4f6",
    border: "1px solid #e5e7eb",
    borderRadius: 7,
    padding: 2,
    gap: 2,
  },
  segBtn: (active) => ({
    fontSize: 11,
    fontWeight: 500,
    padding: "3px 11px",
    borderRadius: 5,
    border: active ? "1px solid #e5e7eb" : "1px solid transparent",
    background: active ? "#fff" : "transparent",
    color: active ? "#1a1d23" : "#6b7280",
    cursor: "pointer",
    whiteSpace: "nowrap",
  }),
  // Pills / badges
  pill: (variant) => {
    const map = {
      red: { background: "#FEF2F2", color: "#991B1B" },
      amber: { background: "#FFFBEB", color: "#92400E" },
      blue: { background: "#EFF6FF", color: "#1E40AF" },
      green: { background: "#F0FDF4", color: "#166534" },
      teal: { background: "#F0FDFA", color: "#115E59" },
      gray: { background: "#F9FAFB", color: "#4B5563", border: "1px solid #e5e7eb" },
    };
    return {
      fontSize: 11,
      fontWeight: 600,
      padding: "2px 9px",
      borderRadius: 20,
      whiteSpace: "nowrap",
      display: "inline-block",
      ...(map[variant] || map.gray),
    };
  },
  // Action button
  actBtn: {
    fontSize: 11,
    fontWeight: 500,
    padding: "4px 12px",
    borderRadius: 20,
    border: "1px solid #d1d5db",
    background: "transparent",
    color: "#374151",
    cursor: "pointer",
  },
  // View breakdown link button
  viewLink: (active) => ({
    fontSize: 11,
    fontWeight: 600,
    color: active ? "#1E40AF" : "#6b7280",
    background: active ? "#EFF6FF" : "transparent",
    border: active ? "1px solid #BFDBFE" : "1px solid transparent",
    borderRadius: 5,
    padding: "2px 8px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  }),
  // Breakdown table
  bdTable: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12,
  },
  bdTh: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#9ca3af",
    padding: "6px 10px",
    textAlign: "left",
    borderBottom: "1px solid #f3f4f6",
    background: "#fafafa",
    whiteSpace: "nowrap",
  },
  bdTd: {
    padding: "7px 10px",
    borderBottom: "1px solid #f3f4f6",
    color: "#374151",
    verticalAlign: "middle",
  },
  // Dot indicator
  dot: (color) => ({
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: color,
    display: "inline-block",
    flexShrink: 0,
  }),
  // Nav card
  navCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "1.125rem",
  },
  navIcon: (bg) => ({
    width: 34,
    height: 34,
    borderRadius: 8,
    background: bg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 10,
  }),
  navBtn: {
    fontSize: 12,
    fontWeight: 500,
    padding: "5px 14px",
    borderRadius: 8,
    border: "1px solid #d1d5db",
    background: "transparent",
    color: "#374151",
    cursor: "pointer",
  },
  // Spinner
  spinner: {
    display: "inline-block",
    width: 14,
    height: 14,
    border: "2px solid #e5e7eb",
    borderTopColor: "#6b7280",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
    verticalAlign: "middle",
    marginRight: 6,
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, "0");
const fmtISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const getPresetRange = (preset) => {
  const now = new Date();
  switch (preset) {
    case "this_month":
      return {
        start: fmtISO(new Date(now.getFullYear(), now.getMonth(), 1)),
        end: fmtISO(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
      };
    case "last_month":
      return {
        start: fmtISO(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
        end: fmtISO(new Date(now.getFullYear(), now.getMonth(), 0)),
      };
    case "this_year":
      return {
        start: fmtISO(new Date(now.getFullYear(), 0, 1)),
        end: fmtISO(new Date(now.getFullYear(), 11, 31)),
      };
    case "all_time":
      return { start: null, end: null };
    default:
      return { start: null, end: null };
  }
};

const presetLabel = (preset, customStart, customEnd) => {
  const now = new Date();
  const monthName = (d) =>
    new Date(d + "T12:00:00").toLocaleDateString("en-US", { month: "long", year: "numeric" });
  switch (preset) {
    case "this_month":
      return now.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    case "last_month": {
      const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    }
    case "this_year":
      return `${now.getFullYear()}`;
    case "all_time":
      return "All time";
    case "custom":
      if (customStart && customEnd) return `${monthName(customStart)} – ${monthName(customEnd)}`;
      return "Custom range";
    default:
      return "";
  }
};

const formatCurrency = (n) =>
  new Intl.NumberFormat("en-TT", {
    style: "currency",
    currency: "TTD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n ?? 0);

const formatDate = (date) => {
  if (!date) return "—";
  // Add 4h to compensate for UTC-4 (Trinidad) so midnight-UTC dates render on the correct calendar day
  const adjusted = new Date(new Date(date).getTime() + 4 * 60 * 60 * 1000);
  return adjusted.toLocaleDateString("en-US", {
    timeZone: "America/Port_of_Spain",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const todayLabel = () =>
  new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "America/Port_of_Spain",
  });

// ── Sub-components ────────────────────────────────────────────────────────────
const Spinner = () => (
  <>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    <span style={s.spinner} />
  </>
);

const Pill = ({ variant, children }) => <span style={s.pill(variant)}>{children}</span>;

const Dot = ({ color }) => <span style={s.dot(color)} />;

const CommValue = ({ label, value, color }) => (
  <div>
    <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 16, fontWeight: 700, color: color || "#1a1d23" }}>{value}</div>
  </div>
);

const IconBox = ({ bg, children }) => <div style={s.navIcon(bg)}>{children}</div>;

// Home icon
const HomeIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#1D4ED8"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

// People icon
const PeopleIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#166534"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

// ── Date helpers ──────────────────────────────────────────────────────────────
const TT_OFFSET_MS = 4 * 60 * 60 * 1000;

const toTrinidadAdjustedDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getTime() + TT_OFFSET_MS);
};

const getTrinidadDateParts = (value) => {
  const adjusted = toTrinidadAdjustedDate(value);
  if (!adjusted) return null;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Port_of_Spain",
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(adjusted);
  const get = (type) => Number(parts.find((p) => p.type === type)?.value || 0);
  return { year: get("year"), month: get("month"), day: get("day") };
};

const getTTTodayStart = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

// ── Due payments builder ──────────────────────────────────────────────────────
const buildDuePaymentsForProperty = (property, mgmtPaidMap) => {
  const rows = [];
  if (!property?._id) return rows;

  if (property.type === "one_time_rental") {
    const tenantInfo = property.placementInfo?.tenantInfo || {};
    const leaseStartParts = getTrinidadDateParts(tenantInfo.leaseStartDate);
    const hasTenantLink = property.placementInfo?.tenantPlaced || Boolean(tenantInfo.tenantId);
    if (!hasTenantLink || !leaseStartParts) return rows;
    if (Boolean(property.pricing?.rentalCommission?.isPaid)) return rows;
    const dueDate = new Date(
      leaseStartParts.year,
      leaseStartParts.month - 1,
      leaseStartParts.day || 1
    );
    rows.push({
      key: `${property._id}-placement-${leaseStartParts.year}-${leaseStartParts.month}`,
      propertyId: property._id,
      propertyTitle: property.title || "Untitled property",
      propertyType: "one_time_rental",
      commissionType: "Placement (1st month)",
      amount: property.pricing?.rentalCommission?.amount ?? property.pricing?.rent ?? 0,
      month: leaseStartParts.month,
      year: leaseStartParts.year,
      dueDate,
    });
    return rows;
  }

  if (property.type !== "property_management") return rows;

  const hasManagedTenant = Boolean(
    property.managementInfo?.currentTenant || property.occupancy?.currentTenant
  );
  const leaseStartRaw = property.managementInfo?.leaseStart || property.occupancy?.leaseStart;
  const leaseEndRaw = property.managementInfo?.leaseEnd || property.occupancy?.leaseEnd;
  const isConverted = !!property.managementInfo?.contractStartDate;
  const placementPaidBefore =
    property.managementInfo?.placementCommissionPaidBeforeConversion === true;
  const commissionStartRaw = isConverted
    ? property.managementInfo.contractStartDate
    : leaseStartRaw;
  const leaseStartParts = getTrinidadDateParts(leaseStartRaw);
  const commissionStartParts = getTrinidadDateParts(commissionStartRaw);
  const contractParts = getTrinidadDateParts(property.managementInfo?.contractStartDate);
  const leaseEndParts = getTrinidadDateParts(leaseEndRaw);
  const monthlyRent = property.pricing?.rent || property.managementInfo?.monthlyRentCollected || 0;
  const ongoingRate =
    property.pricing?.rentalCommission?.ongoingRate != null
      ? property.pricing.rentalCommission.ongoingRate
      : 5;
  if (!hasManagedTenant || !commissionStartParts || monthlyRent <= 0) return rows;

  const nowParts = getTrinidadDateParts(new Date());
  const useLeaseEnd =
    leaseEndParts &&
    (leaseEndParts.year < nowParts.year ||
      (leaseEndParts.year === nowParts.year && leaseEndParts.month < nowParts.month));
  const endY = useLeaseEnd ? leaseEndParts.year : nowParts.year;
  const endM = useLeaseEnd ? leaseEndParts.month : nowParts.month;
  const dueDay = leaseStartParts ? leaseStartParts.day || 1 : 1;
  const nativeFirstMonthKey = leaseStartParts
    ? `${leaseStartParts.year}-${leaseStartParts.month}`
    : null;
  const contractMonthKey =
    contractParts && isConverted ? `${contractParts.year}-${contractParts.month}` : null;

  let m, y;
  if (isConverted && placementPaidBefore && contractParts) {
    const nm =
      contractParts.month === 12
        ? { month: 1, year: contractParts.year + 1 }
        : { month: contractParts.month + 1, year: contractParts.year };
    m = nm.month;
    y = nm.year;
  } else {
    m = commissionStartParts.month;
    y = commissionStartParts.year;
  }

  while (y < endY || (y === endY && m <= endM)) {
    const paidKey = `${property._id}-${y}-${m}`;
    if (!mgmtPaidMap.has(paidKey)) {
      const key = `${y}-${m}`;
      const isConvertedUnpaidPlacement = Boolean(
        isConverted && !placementPaidBefore && contractMonthKey && key === contractMonthKey
      );
      const isFirstMonth =
        (!isConverted && key === nativeFirstMonthKey) || isConvertedUnpaidPlacement;
      const amount = isFirstMonth ? monthlyRent : monthlyRent * (ongoingRate / 100);
      const dayInMonth = Math.min(dueDay, new Date(y, m, 0).getDate());
      rows.push({
        key: `${property._id}-mgmt-${key}`,
        propertyId: property._id,
        propertyTitle: property.title || "Untitled property",
        propertyType: "property_management",
        commissionType: isFirstMonth ? "Placement (1st month)" : `Ongoing (${ongoingRate}%)`,
        amount,
        month: m,
        year: y,
        dueDate: new Date(y, m - 1, dayInMonth),
      });
    }
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return rows;
};

// ── Presets config ────────────────────────────────────────────────────────────
const PRESETS = [
  { id: "this_month", label: "This month" },
  { id: "last_month", label: "Last month" },
  { id: "this_year", label: "This year" },
  { id: "all_time", label: "All time" },
  { id: "custom", label: "Custom" },
];

// ── Main component ────────────────────────────────────────────────────────────
function Dashboard() {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();

  // Commission range state
  const [rangePreset, setRangePreset] = useState("this_month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const activeRange = useMemo(() => {
    if (rangePreset === "custom") return { start: customStart, end: customEnd };
    return getPresetRange(rangePreset);
  }, [rangePreset, customStart, customEnd]);

  // Commission data
  const [commission, setCommission] = useState(null);
  const [managementCommission, setManagementCommission] = useState(null);
  const [saleCommission, setSaleCommission] = useState(null);
  const [commissionLoading, setCommissionLoading] = useState(true);

  // Property / tenant stats
  const [propertyStats, setPropertyStats] = useState(null);
  const [tenantStats, setTenantStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Due payments
  const [duePayments, setDuePayments] = useState([]);
  const [duePaymentsLoading, setDuePaymentsLoading] = useState(true);

  // Maintenance tasks
  const [maintenanceTasks, setMaintenanceTasks] = useState([]);
  const [maintenanceLoading, setMaintenanceLoading] = useState(true);

  // ── Fetch commission overview ──────────────────────────────────────────────
  const fetchOverview = useCallback(async () => {
    setCommissionLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeRange.start) params.set("startDate", activeRange.start);
      if (activeRange.end) params.set("endDate", activeRange.end);
      const res = await fetch(API_ENDPOINTS.financials.getOverview(params.toString()), {
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
      });
      if (res.ok) {
        const data = await res.json();
        setCommission(data.data?.commission ?? null);
        setManagementCommission(data.data?.managementCommission ?? null);
        setSaleCommission(data.data?.saleCommission ?? null);
      }
    } catch {
      /* ignore */
    } finally {
      setCommissionLoading(false);
    }
  }, [activeRange, getAuthHeaders]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  // ── Fetch property + tenant stats ─────────────────────────────────────────
  useEffect(() => {
    const headers = {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
      ...getAuthHeaders(),
    };
    let cancelled = false;
    (async () => {
      setStatsLoading(true);
      try {
        const [propRes, tenantRes] = await Promise.all([
          fetch(API_ENDPOINTS.properties.getStats, { headers }),
          fetch(API_ENDPOINTS.tenants.getStats, { headers }),
        ]);
        if (cancelled) return;
        if (propRes.ok) {
          const d = await propRes.json();
          setPropertyStats(d.data ?? null);
        }
        if (tenantRes.ok) {
          const d = await tenantRes.json();
          setTenantStats(d.data ?? null);
        }
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getAuthHeaders]);

  // ── Fetch due payments ────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setDuePaymentsLoading(true);
      try {
        const headers = {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        };
        const [paymentsRes, firstPage] = await Promise.all([
          fetch(`${API_ENDPOINTS.payments.getAll}?type=management_fee`, { headers }).catch(
            () => null
          ),
          fetch(`${API_ENDPOINTS.properties.getAll}?page=1&limit=50`, { headers }),
        ]);
        if (cancelled) return;

        const mgmtPaidMap = new Map();
        if (paymentsRes?.ok) {
          const pData = await paymentsRes.json();
          for (const payment of pData.data || []) {
            const key = `${payment.property?._id || payment.property}-${payment.year}-${payment.month}`;
            mgmtPaidMap.set(key, true);
          }
        }

        const allProperties = [];
        if (firstPage?.ok) {
          const data = await firstPage.json();
          allProperties.push(...(Array.isArray(data?.data) ? data.data : []));
          const totalPages = Number(data?.pagination?.pages || 1);
          if (totalPages > 1) {
            const rest = await Promise.all(
              Array.from({ length: totalPages - 1 }, (_, i) =>
                fetch(`${API_ENDPOINTS.properties.getAll}?page=${i + 2}&limit=50`, { headers })
              )
            );
            for (const res of rest) {
              if (res.ok) {
                const d = await res.json();
                allProperties.push(...(Array.isArray(d?.data) ? d.data : []));
              }
            }
          }
        }

        const rows = allProperties
          .flatMap((p) => buildDuePaymentsForProperty(p, mgmtPaidMap))
          .sort((a, b) => a.dueDate - b.dueDate);

        if (!cancelled) setDuePayments(rows);
      } catch {
        if (!cancelled) setDuePayments([]);
      } finally {
        if (!cancelled) setDuePaymentsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getAuthHeaders]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch maintenance tasks ───────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setMaintenanceLoading(true);
      try {
        const headers = {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        };
        const res = await fetch(API_ENDPOINTS.maintenanceTasks.listAll("open"), { headers });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const tasks = (data.data || []).map((t) => ({
          ...t,
          propertyId: t.property?._id || t.property,
          propertyTitle: t.property?.title || "Untitled property",
        }));
        if (!cancelled) setMaintenanceTasks(tasks);
      } catch {
        if (!cancelled) setMaintenanceTasks([]);
      } finally {
        if (!cancelled) setMaintenanceLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getAuthHeaders]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived values ────────────────────────────────────────────────────────
  const todayStart = useMemo(() => getTTTodayStart(), []);

  const dueSummary = useMemo(() => {
    const summary = { overdue: 0, open: 0, totalAtRisk: 0 };
    for (const row of duePayments) {
      if (row.dueDate < todayStart) {
        summary.overdue += 1;
        summary.totalAtRisk += row.amount;
      } else summary.open += 1;
    }
    return summary;
  }, [duePayments, todayStart]);

  const maintenanceSummary = useMemo(() => {
    const now = new Date();
    let overdue = 0;
    let urgent = 0;
    let high = 0;
    for (const t of maintenanceTasks) {
      if (t.dueDate) {
        const end = new Date(t.dueDate);
        end.setHours(23, 59, 59, 999);
        if (end < now) overdue += 1;
      }
      if (t.priority === "urgent") urgent += 1;
      if (t.priority === "high") high += 1;
    }
    return { overdue, urgent, high };
  }, [maintenanceTasks]);

  const totalCommissionEarned = useMemo(() => {
    let total = 0;
    if (commission?.placement?.paid) total += commission.placement.paid;
    if (managementCommission?.paid) total += managementCommission.paid;
    if (saleCommission?.paid) total += saleCommission.paid;
    return total;
  }, [commission, managementCommission, saleCommission]);

  const showCommission = commission || managementCommission || saleCommission;

  // ── Commission breakdown panel ────────────────────────────────────────────
  const BD_PAGE_SIZE = 8;
  const [breakdownType, setBreakdownType] = useState(null); // "rent_placement" | "management_fee" | "sale_commission"
  const [breakdownPayments, setBreakdownPayments] = useState([]);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownCache, setBreakdownCache] = useState({}); // type → payments[]
  const [breakdownPage, setBreakdownPage] = useState(1);

  // Reset breakdown when date range changes
  useEffect(() => {
    setBreakdownType(null);
    setBreakdownPayments([]);
    setBreakdownCache({});
    setBreakdownPage(1);
  }, [activeRange]);

  const fetchBreakdown = useCallback(
    async (type) => {
      // Toggle off if already showing
      if (breakdownType === type) {
        setBreakdownType(null);
        return;
      }
      setBreakdownType(type);
      setBreakdownPage(1);

      // Use cache if already fetched for this range
      if (breakdownCache[type]) {
        setBreakdownPayments(breakdownCache[type]);
        return;
      }

      setBreakdownLoading(true);
      try {
        const res = await fetch(`${API_ENDPOINTS.payments.getAll}?type=${type}`, {
          headers: {
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "true",
            ...getAuthHeaders(),
          },
        });
        if (!res.ok) throw new Error(`Error ${res.status}`);
        const data = await res.json();
        const all = data.data || [];

        // Client-side filter to active range
        const filtered = all.filter((p) => {
          if (!p.paidAt) return false;
          const d = new Date(p.paidAt);
          if (activeRange.start && d < new Date(activeRange.start + "T00:00:00")) return false;
          if (activeRange.end && d > new Date(activeRange.end + "T23:59:59.999")) return false;
          return true;
        });

        setBreakdownPayments(filtered);
        setBreakdownCache((prev) => ({ ...prev, [type]: filtered }));
      } catch {
        setBreakdownPayments([]);
      } finally {
        setBreakdownLoading(false);
      }
    },
    [breakdownType, breakdownCache, activeRange, getAuthHeaders]
  );

  // ── Navigation ────────────────────────────────────────────────────────────
  const goProperties = () => navigate("/properties");
  const goTenants = () => navigate("/tenants");
  const goFinancials = (id) => navigate(`/properties/propertyDetails/${id}?tab=Financials`);
  const goMaintenance = (id) => navigate(`/properties/propertyDetails/${id}?tab=Maintenance`);

  // ── Priority pill ─────────────────────────────────────────────────────────
  const priorityPill = (priority) => {
    const map = { urgent: "red", high: "amber", medium: "blue", low: "gray" };
    const label = priority ? priority.charAt(0).toUpperCase() + priority.slice(1) : "Medium";
    return <Pill variant={map[priority] || "gray"}>{label}</Pill>;
  };

  // ── Task date helpers ─────────────────────────────────────────────────────
  const getTaskStatus = (dueDate) => {
    if (!dueDate) return { isOverdue: false, isToday: false };
    const [y, m, d] = dueDate.slice(0, 10).split("-").map(Number);
    const end = new Date(y, m - 1, d);
    end.setHours(23, 59, 59, 999);
    const day = new Date(y, m - 1, d);
    const now = new Date();
    const isOverdue = end < now;
    const isToday =
      !isOverdue &&
      day.toDateString() ===
        new Date(now.getFullYear(), now.getMonth(), now.getDate()).toDateString();
    return { isOverdue, isToday };
  };

  const fmtTaskDate = (dueDate) => {
    if (!dueDate) return "—";
    const [y, m, d] = dueDate.slice(0, 10).split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={s.page} className="dash-page">
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @media (max-width: 768px){
          /* Reclaim horizontal space on phones */
          .dash-page{
            padding: 1.25rem 0.75rem !important;
            /* Hard guard: no child may make the page scroll sideways. */
            overflow-x: hidden !important;
            max-width: 100vw !important;
          }
          /* Slightly smaller headline so it doesn't wrap awkwardly */
          .dash-page .dash-title{ font-size: 18px !important; }
          /* Grids that were hard-coded to 3 columns stack on narrow screens */
          .dash-page .dash-grid-3{ grid-template-columns: 1fr 1fr !important; }
          /* KPI grids: allow a smaller min so they never overflow the viewport
             (140px min * 2 + gap can exceed a narrow phone). Only the small-tile
             grids — wider content grids stack via .re-stack-sm instead. */
          .dash-page [style*="minmax(140px"],
          .dash-page [style*="minmax(160px"]{
            grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)) !important;
          }
          /* The date-range segmented control (This month … Custom) is wider than
             a phone. Let its buttons wrap to a second line so every option —
             including "Custom" — is fully visible instead of being clipped. */
          .dash-page .dash-seg{
            flex-wrap: wrap !important;
            max-width: 100%;
          }
        }
        @media (max-width: 480px){
          .dash-page{ padding: 1rem 0.5rem !important; }
          .dash-page .dash-grid-3{ grid-template-columns: 1fr !important; }
        }
        /* Tables use tableLayout:fixed, which crushes their many columns into
           the phone width and makes every cell an unreadable sliver. Give them
           a readable min-width so the .dash-scroll wrapper genuinely scrolls
           instead. Momentum scrolling makes the swipe feel native. */
        @media (max-width: 768px){
          .dash-page .dash-scroll{ -webkit-overflow-scrolling: touch; }
          .dash-page .dash-scroll > table{
            table-layout: auto !important;
            min-width: 640px;
          }
          /* colgroup percentage widths fight table-layout:auto — neutralise them */
          .dash-page .dash-scroll > table col{ width: auto !important; }
        }
      `}</style>

      {/* ── Header ── */}
      <div style={{ marginBottom: "1.75rem" }}>
        <div style={s.pageTitle} className="dash-title">Portfolio overview</div>
        <div style={s.pageSub}>{todayLabel()} &nbsp;·&nbsp; Trinidad &amp; Tobago</div>
      </div>

      {/* ── KPI strip ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: 10,
          marginBottom: "1.25rem",
        }}
      >
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Total properties</div>
          {statsLoading ? <Spinner /> : <div style={s.kpiValue}>{propertyStats?.total ?? 0}</div>}
          <div style={s.kpiSub}>
            {propertyStats
              ? `${propertyStats.managed ?? "—"} managed · ${propertyStats.rental ?? "—"} rental`
              : "—"}
          </div>
        </div>

        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Active tenants</div>
          {statsLoading ? <Spinner /> : <div style={s.kpiValue}>{tenantStats?.active ?? 0}</div>}
        </div>

        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Overdue payments</div>
          {duePaymentsLoading ? (
            <Spinner />
          ) : (
            <div style={{ ...s.kpiValue, color: dueSummary.overdue > 0 ? "#991B1B" : "#1a1d23" }}>
              {dueSummary.overdue}
            </div>
          )}
          <div style={s.kpiSub}>
            {dueSummary.overdue > 0
              ? `${formatCurrency(dueSummary.totalAtRisk)} at risk`
              : "All payments current"}
          </div>
        </div>

        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Open tasks</div>
          {maintenanceLoading ? (
            <Spinner />
          ) : (
            <div
              style={{
                ...s.kpiValue,
                color: maintenanceSummary.overdue > 0 ? "#92400E" : "#1a1d23",
              }}
            >
              {maintenanceTasks.length}
            </div>
          )}
          <div style={s.kpiSub}>
            {maintenanceSummary.urgent > 0 || maintenanceSummary.high > 0
              ? `${maintenanceSummary.urgent} urgent · ${maintenanceSummary.high} high`
              : "No urgent items"}
          </div>
        </div>
      </div>

      {/* ── Commission ── */}
      <div style={{ ...s.card, marginBottom: "1.25rem" }}>
        <div style={s.cardHead}>
          <div style={s.cardHeadTitle}>
            <Dot color="#185FA5" />
            Commission summary
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div style={s.seg} className="dash-seg">
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  style={s.segBtn(rangePreset === p.id)}
                  onClick={() => setRangePreset(p.id)}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {rangePreset === "custom" && (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  style={{
                    fontSize: 12,
                    padding: "3px 8px",
                    border: "1px solid #d1d5db",
                    borderRadius: 6,
                    color: "#1a1d23",
                    background: "#fff",
                  }}
                />
                <span style={{ fontSize: 12, color: "#9ca3af" }}>–</span>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  style={{
                    fontSize: 12,
                    padding: "3px 8px",
                    border: "1px solid #d1d5db",
                    borderRadius: 6,
                    color: "#1a1d23",
                    background: "#fff",
                  }}
                />
              </div>
            )}
          </div>
        </div>

        <div style={s.cardBody}>
          <div style={{ ...s.sectionLabel, marginBottom: "1rem" }}>
            {presetLabel(rangePreset, customStart, customEnd)}
          </div>

          {commissionLoading ? (
            <div style={{ padding: "1rem 0", color: "#6b7280", fontSize: 13 }}>
              <Spinner /> Loading commission data…
            </div>
          ) : showCommission ? (
            <>
              <div
                className="re-stack-sm"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: "1rem",
                }}
              >
                {/* One-time rental */}
                {commission && (
                  <div style={{ borderRight: "1px solid #e5e7eb", paddingRight: "1rem" }}>
                    <div
                      style={{
                        ...s.sectionLabel,
                        paddingBottom: "0.5rem",
                        borderBottom: "1px solid #e5e7eb",
                        marginBottom: "0.75rem",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span>One-time rental</span>
                      <button
                        style={s.viewLink(breakdownType === "rent_placement")}
                        onClick={() => fetchBreakdown("rent_placement")}
                      >
                        {breakdownType === "rent_placement" ? "Hide ↑" : "View payments →"}
                      </button>
                    </div>
                    <div className="dash-grid-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                      <CommValue
                        label="Placement paid"
                        value={formatCurrency(commission.placement?.paid)}
                        color="#166534"
                      />
                      <CommValue
                        label="Pending"
                        value={formatCurrency(commission.placement?.pending)}
                        color="#92400E"
                      />
                      <CommValue label="Total" value={formatCurrency(commission.total)} />
                    </div>
                  </div>
                )}

                {/* Property management */}
                {managementCommission && (
                  <div
                    style={{
                      borderRight: saleCommission ? "1px solid #e5e7eb" : "none",
                      paddingRight: saleCommission ? "1rem" : 0,
                    }}
                  >
                    <div
                      style={{
                        ...s.sectionLabel,
                        paddingBottom: "0.5rem",
                        borderBottom: "1px solid #e5e7eb",
                        marginBottom: "0.75rem",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span>Property management</span>
                      <button
                        style={s.viewLink(breakdownType === "management_fee")}
                        onClick={() => fetchBreakdown("management_fee")}
                      >
                        {breakdownType === "management_fee" ? "Hide ↑" : "View payments →"}
                      </button>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>
                        Paid in period
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: "#1E40AF" }}>
                        {formatCurrency(managementCommission.paid)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Sale */}
                {saleCommission && (
                  <div>
                    <div
                      style={{
                        ...s.sectionLabel,
                        paddingBottom: "0.5rem",
                        borderBottom: "1px solid #e5e7eb",
                        marginBottom: "0.75rem",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span>Sale</span>
                      <button
                        style={s.viewLink(breakdownType === "sale_commission")}
                        onClick={() => fetchBreakdown("sale_commission")}
                      >
                        {breakdownType === "sale_commission" ? "Hide ↑" : "View payments →"}
                      </button>
                    </div>
                    <div className="dash-grid-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                      <CommValue
                        label="Paid"
                        value={formatCurrency(saleCommission.paid)}
                        color="#166534"
                      />
                      <CommValue
                        label="Pending"
                        value={formatCurrency(saleCommission.pending)}
                        color="#92400E"
                      />
                      <CommValue label="Total" value={formatCurrency(saleCommission.total)} />
                    </div>
                  </div>
                )}
              </div>

              {/* ── Breakdown panel ── */}
              {breakdownType && (
                <div
                  style={{
                    marginTop: "1rem",
                    borderTop: "1px solid #e5e7eb",
                    paddingTop: "0.875rem",
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#374151",
                      marginBottom: "0.625rem",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span>
                      {breakdownType === "rent_placement" && "One-time rental – payment records"}
                      {breakdownType === "management_fee" &&
                        "Property management – payment records"}
                      {breakdownType === "sale_commission" && "Sale – payment records"}
                    </span>
                    {!breakdownLoading && (
                      <span style={{ ...s.pill("gray"), fontWeight: 500 }}>
                        {breakdownPayments.length} record{breakdownPayments.length !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>

                  {breakdownLoading ? (
                    <div style={{ fontSize: 13, color: "#6b7280", padding: "0.5rem 0" }}>
                      <Spinner /> Loading payments…
                    </div>
                  ) : breakdownPayments.length === 0 ? (
                    <div style={{ fontSize: 13, color: "#9ca3af", padding: "0.5rem 0" }}>
                      No paid records found for this period.
                    </div>
                  ) : (
                    (() => {
                      const bdTotal = breakdownPayments.length;
                      const bdTotalPages = Math.ceil(bdTotal / BD_PAGE_SIZE);
                      const bdStart = (breakdownPage - 1) * BD_PAGE_SIZE;
                      const bdEnd = Math.min(bdStart + BD_PAGE_SIZE, bdTotal);
                      const bdSlice = breakdownPayments.slice(bdStart, bdEnd);
                      return (
                        <>
                          <div
                            className="dash-scroll"
                            style={{
                              overflowX: "auto",
                              borderRadius: 8,
                              border: "1px solid #e5e7eb",
                            }}
                          >
                            <table style={s.bdTable}>
                              <thead>
                                <tr>
                                  {[
                                    "Property",
                                    "Tenant",
                                    "Month / Year",
                                    "Amount",
                                    "Paid On",
                                    "Notes",
                                  ].map((h) => (
                                    <th key={h} style={s.bdTh}>
                                      {h}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {bdSlice.map((p) => {
                                  const tenantName = p.tenant
                                    ? `${p.tenant.personalInfo?.firstName || ""} ${p.tenant.personalInfo?.lastName || ""}`.trim() ||
                                      "—"
                                    : "—";
                                  const monthLabel = p.month
                                    ? new Date(2000, p.month - 1, 1).toLocaleString("en-US", {
                                        month: "short",
                                      })
                                    : "—";
                                  return (
                                    <tr
                                      key={p._id}
                                      style={{ cursor: "pointer" }}
                                      onClick={() =>
                                        p.property?._id && goFinancials(p.property._id)
                                      }
                                    >
                                      <td style={{ ...s.bdTd, fontWeight: 600, color: "#111827" }}>
                                        {p.property?.title || "—"}
                                        {p.property?.address?.city && (
                                          <div
                                            style={{
                                              fontSize: 11,
                                              color: "#9ca3af",
                                              fontWeight: 400,
                                            }}
                                          >
                                            {p.property.address.city}
                                          </div>
                                        )}
                                      </td>
                                      <td style={s.bdTd}>{tenantName}</td>
                                      <td style={s.bdTd}>
                                        {monthLabel} {p.year}
                                      </td>
                                      <td style={{ ...s.bdTd, fontWeight: 600, color: "#166534" }}>
                                        {formatCurrency(p.amount)}
                                      </td>
                                      <td style={{ ...s.bdTd, color: "#6b7280" }}>
                                        {formatDate(p.paidAt)}
                                      </td>
                                      <td
                                        style={{
                                          ...s.bdTd,
                                          color: "#9ca3af",
                                          maxWidth: 160,
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {p.notes || "—"}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>

                          {/* Pagination controls */}
                          {bdTotalPages > 1 && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginTop: 8,
                                fontSize: 11,
                                color: "#6b7280",
                              }}
                            >
                              <span>
                                Showing {bdStart + 1}–{bdEnd} of {bdTotal}
                              </span>
                              <div style={{ display: "flex", gap: 4 }}>
                                <button
                                  style={{
                                    ...s.actBtn,
                                    opacity: breakdownPage === 1 ? 0.4 : 1,
                                    cursor: breakdownPage === 1 ? "default" : "pointer",
                                  }}
                                  disabled={breakdownPage === 1}
                                  onClick={() => setBreakdownPage((p) => p - 1)}
                                >
                                  ← Prev
                                </button>
                                {Array.from({ length: bdTotalPages }, (_, i) => i + 1).map((pg) => (
                                  <button
                                    key={pg}
                                    style={{
                                      fontSize: 11,
                                      fontWeight: pg === breakdownPage ? 700 : 400,
                                      padding: "4px 8px",
                                      borderRadius: 20,
                                      border:
                                        pg === breakdownPage
                                          ? "1px solid #1E40AF"
                                          : "1px solid #d1d5db",
                                      background: pg === breakdownPage ? "#EFF6FF" : "transparent",
                                      color: pg === breakdownPage ? "#1E40AF" : "#6b7280",
                                      cursor: "pointer",
                                    }}
                                    onClick={() => setBreakdownPage(pg)}
                                  >
                                    {pg}
                                  </button>
                                ))}
                                <button
                                  style={{
                                    ...s.actBtn,
                                    opacity: breakdownPage === bdTotalPages ? 0.4 : 1,
                                    cursor: breakdownPage === bdTotalPages ? "default" : "pointer",
                                  }}
                                  disabled={breakdownPage === bdTotalPages}
                                  onClick={() => setBreakdownPage((p) => p + 1)}
                                >
                                  Next →
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()
                  )}
                </div>
              )}

              {/* Total footer */}
              <div
                style={{
                  marginTop: "1rem",
                  paddingTop: "0.875rem",
                  borderTop: "1px solid #e5e7eb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ fontSize: 12, color: "#6b7280" }}>Total earned this period</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#1a1d23" }}>
                  {formatCurrency(totalCommissionEarned)}
                </div>
              </div>
            </>
          ) : (
            <div style={{ fontSize: 13, color: "#9ca3af" }}>
              No commission data for this period.
            </div>
          )}
        </div>
      </div>

      {/* ── Due payments ── */}
      <div style={{ ...s.card, marginBottom: "1.25rem" }}>
        <div style={s.cardHead}>
          <div style={s.cardHeadTitle}>
            <Dot color="#B45309" />
            Due monthly payments
          </div>
          {!duePaymentsLoading && (
            <div style={{ display: "flex", gap: 6 }}>
              <Pill variant="red">{dueSummary.overdue} overdue</Pill>
              <Pill variant="gray">{dueSummary.open} open</Pill>
            </div>
          )}
        </div>

        {duePaymentsLoading ? (
          <div style={{ padding: "1rem 1.125rem", fontSize: 13, color: "#6b7280" }}>
            <Spinner /> Loading due payments…
          </div>
        ) : duePayments.length === 0 ? (
          <div style={{ padding: "1rem 1.125rem", fontSize: 13, color: "#9ca3af" }}>
            No outstanding monthly payments right now.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table
              className="re-table"
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
                tableLayout: "fixed",
              }}
            >
              <colgroup>
                <col style={{ width: "20%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "5%" }} />
              </colgroup>
              <thead>
                <tr>
                  {[
                    "Property",
                    "Type",
                    "Month",
                    "Due date",
                    "Commission",
                    "Amount",
                    "Status",
                    "",
                  ].map((h, i) => (
                    <th key={i} style={{ ...s.th, textAlign: i === 7 ? "right" : "left" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {duePayments.map((row) => {
                  const isOverdue = row.dueDate < todayStart;
                  const isToday =
                    !isOverdue && row.dueDate.toDateString() === todayStart.toDateString();
                  return (
                    <tr key={row.key}>
                      <td
                        data-label="Property"
                        style={{
                          ...s.td,
                          fontWeight: 600,
                          borderLeft: isOverdue ? "3px solid #DC2626" : "3px solid transparent",
                        }}
                      >
                        {row.propertyTitle}
                      </td>
                      <td data-label="Type" style={s.td}>
                        <Pill variant={row.propertyType === "one_time_rental" ? "blue" : "teal"}>
                          {row.propertyType === "one_time_rental" ? "One-time" : "Managed"}
                        </Pill>
                      </td>
                      <td data-label="Month" style={s.td}>
                        {new Date(2000, row.month - 1, 1).toLocaleString("en-US", {
                          month: "short",
                        })}{" "}
                        {row.year}
                      </td>
                      <td data-label="Due date" style={{ ...s.td, color: isOverdue ? "#DC2626" : "inherit" }}>
                        {formatDate(row.dueDate)}
                      </td>
                      <td data-label="Commission" style={{ ...s.td, color: "#6b7280" }}>{row.commissionType}</td>
                      <td data-label="Amount" style={{ ...s.td, fontWeight: 600 }}>{formatCurrency(row.amount)}</td>
                      <td data-label="Status" style={s.td}>
                        {isOverdue ? (
                          <Pill variant="red">Overdue</Pill>
                        ) : isToday ? (
                          <Pill variant="amber">Due today</Pill>
                        ) : (
                          <Pill variant="gray">Upcoming</Pill>
                        )}
                      </td>
                      <td data-label="" style={{ ...s.td, textAlign: "right" }}>
                        <button style={s.actBtn} onClick={() => goFinancials(row.propertyId)}>
                          Open →
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Maintenance tasks ── */}
      <div style={{ ...s.card, marginBottom: "1.25rem" }}>
        <div style={s.cardHead}>
          <div style={s.cardHeadTitle}>
            <Dot color="#DC2626" />
            Maintenance tasks
          </div>
          {!maintenanceLoading && (
            <div style={{ display: "flex", gap: 6 }}>
              <Pill variant="red">{maintenanceSummary.overdue} overdue</Pill>
              <Pill variant="gray">{maintenanceTasks.length} open</Pill>
            </div>
          )}
        </div>

        {maintenanceLoading ? (
          <div style={{ padding: "1rem 1.125rem", fontSize: 13, color: "#6b7280" }}>
            <Spinner /> Loading maintenance tasks…
          </div>
        ) : maintenanceTasks.length === 0 ? (
          <div style={{ padding: "1rem 1.125rem", fontSize: 13, color: "#9ca3af" }}>
            No open maintenance tasks right now.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table
              className="re-table"
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
                tableLayout: "fixed",
              }}
            >
              <colgroup>
                <col style={{ width: "22%" }} />
                <col style={{ width: "30%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "9%" }} />
              </colgroup>
              <thead>
                <tr>
                  {["Property", "Task", "Priority", "Due date", "Status", ""].map((h, i) => (
                    <th key={i} style={{ ...s.th, textAlign: i === 5 ? "right" : "left" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {maintenanceTasks.map((task) => {
                  const { isOverdue, isToday } = getTaskStatus(task.dueDate);
                  return (
                    <tr
                      key={task._id}
                      style={{ cursor: "pointer" }}
                      onClick={() => goMaintenance(task.propertyId)}
                    >
                      <td
                        data-label="Property"
                        style={{
                          ...s.td,
                          fontWeight: 600,
                          borderLeft: isOverdue ? "3px solid #DC2626" : "3px solid transparent",
                        }}
                      >
                        {task.propertyTitle}
                      </td>
                      <td data-label="Task" style={{ ...s.td, color: "#6b7280" }}>{task.title}</td>
                      <td data-label="Priority" style={s.td}>{priorityPill(task.priority)}</td>
                      <td data-label="Due date" style={{ ...s.td, color: isOverdue ? "#DC2626" : "inherit" }}>
                        {fmtTaskDate(task.dueDate)}
                      </td>
                      <td data-label="Status" style={s.td}>
                        {isOverdue ? (
                          <Pill variant="red">Overdue</Pill>
                        ) : isToday ? (
                          <Pill variant="amber">Due today</Pill>
                        ) : task.dueDate ? (
                          <Pill variant="gray">Upcoming</Pill>
                        ) : (
                          <Pill variant="gray">No date</Pill>
                        )}
                      </td>
                      <td data-label="" style={{ ...s.td, textAlign: "right" }}>
                        <button
                          style={s.actBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            goMaintenance(task.propertyId);
                          }}
                        >
                          View →
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Nav cards ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 10,
        }}
      >
        <div style={s.navCard}>
          <IconBox bg="#EFF6FF">
            <HomeIcon />
          </IconBox>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Properties</div>
          <div
            style={{ fontSize: 13, color: "#6b7280", marginBottom: "0.875rem", lineHeight: 1.5 }}
          >
            View, add, and manage all your properties. Track financials, maintenance, and tenants.
          </div>
          <button style={s.navBtn} onClick={goProperties}>
            Manage properties →
          </button>
        </div>

        <div style={s.navCard}>
          <IconBox bg="#F0FDF4">
            <PeopleIcon />
          </IconBox>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Tenants</div>
          <div
            style={{ fontSize: 13, color: "#6b7280", marginBottom: "0.875rem", lineHeight: 1.5 }}
          >
            Manage tenant info, lease agreements, rent payments, and communication records.
          </div>
          <button style={s.navBtn} onClick={goTenants}>
            Manage tenants →
          </button>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
